import axios from "axios";
import { API_BASE_URL } from '../../config/apiConfig';

// ==============================================
// GENERIC S3 DOCUMENT UPLOAD
// Backed by POST /AWSUpload/UploadFile (multipart/form-data)
// Reusable by any module that needs to attach a document —
// pass the S3 folder name (see config/s3Config.js S3_FOLDERS)
// and the file the user picked.
// ==============================================

/**
 * Upload a single file to S3 under "Upload docs/<folder>/<fileName>".
 * @param {File} file - the File object from an <input type="file">
 * @param {string} folder - S3 folder name, e.g. "VendorDetailsPROD"
 * @param {string} [fileName] - optional override; defaults to file.name
 * @param {(percent: number) => void} [onProgress] - called with 0-100 as the upload advances
 * @returns {Promise<{success:boolean, message:string, path:string, fileName:string, url:string}>}
 */
export const uploadFileToS3 = async (file, folder, fileName, onProgress) => {
    try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('folder', folder);
        formData.append('fileName', fileName || file.name);

        const response = await axios.post(
            `${API_BASE_URL}/AWSUpload/UploadFile`,
            formData,
            {
                // Deliberately no Content-Type header here: setting one manually (even
                // to "multipart/form-data") strips the boundary the browser would
                // otherwise attach, which breaks server-side multipart parsing and
                // makes every upload fail regardless of file size. Let axios/the
                // browser generate the header (with boundary) from the FormData body.
                timeout: 5 * 60 * 1000, // large docs over a slow connection need real headroom
                onUploadProgress: onProgress
                    ? (evt) => {
                        if (!evt.total) return;
                        onProgress(Math.round((evt.loaded / evt.total) * 100));
                    }
                    : undefined,
            }
        );
        return response.data;
    } catch (error) {
        console.error('❌ Upload File To S3 API Error:', error.response || error);
        if (error.response?.data) {
            throw error.response.data;
        }
        throw error;
    }
};
